import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useAccount } from '../contexts/AccountContext';
import { firestore } from '../services/firebase';
import type {
  Assessment,
  Evaluation,
  EvaluatorAssignment,
  Procurement,
  QuickAnalysis,
  Rubric,
  Supplier,
} from '../types/procurement';

export const useProcurementDetails = (procurementId: string | undefined) => {
  const { account } = useAccount();
  const [procurement, setProcurement] = useState<Procurement | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [rubric, setRubric] = useState<Rubric | null>(null);
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [quickAnalyses, setQuickAnalyses] = useState<QuickAnalysis[]>([]);
  const [evaluatorAssignments, setEvaluatorAssignments] = useState<EvaluatorAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const isPanelChair =
    procurement && account
      ? account.uid === procurement.ownerId ||
        account.role === 'Admin' ||
        account.role === 'Super Admin' ||
        account.role === 'client_admin'
      : false;

  useEffect(() => {
    if (!procurementId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Document listener for the procurement itself
    const unsubProcurement = onSnapshot(
      doc(firestore, 'procurements', procurementId),
      (docSnap) => {
        if (docSnap.exists()) {
          setProcurement({ id: docSnap.id, ...docSnap.data() } as Procurement);
        } else {
          setProcurement(null);
        }
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );

    // Suppliers listener
    const unsubSuppliers = onSnapshot(
      collection(firestore, 'procurements', procurementId, 'suppliers'),
      (snapshot) => {
        setSuppliers(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Supplier));
      },
      (err) => setError(err)
    );

    // Rubric listener
    const unsubRubric = onSnapshot(
      doc(firestore, 'procurements', procurementId, 'rubric', 'default'),
      (docSnap) => {
        if (docSnap.exists()) {
          setRubric({ id: docSnap.id, ...docSnap.data() } as Rubric);
        } else {
          setRubric(null);
        }
      },
      (_err) => {
        // Do not set global error for missing rubric as it might be created later
      }
    );

    // Assessments listener
    const unsubAssessments = onSnapshot(
      collection(firestore, 'procurements', procurementId, 'assessments'),
      (snapshot) => {
        setAssessments(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Assessment));
      },
      (err) => setError(err)
    );

    // EvaluatorAssignments listener
    const unsubEvaluatorAssignments = onSnapshot(
      collection(firestore, 'procurements', procurementId, 'evaluatorAssignments'),
      (snapshot) => {
        setEvaluatorAssignments(
          snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as EvaluatorAssignment)
        );
      },
      (err) => setError(err)
    );

    return () => {
      unsubProcurement();
      unsubSuppliers();
      unsubRubric();
      unsubAssessments();
      unsubEvaluatorAssignments();
    };
  }, [procurementId]);

  // Separate effect for Quick Analyses to handle RBAC filtering based on panel chair status
  useEffect(() => {
    if (!procurementId || !isPanelChair) {
      setQuickAnalyses([]);
      return;
    }

    const unsubQuickAnalyses = onSnapshot(
      collection(firestore, 'procurements', procurementId, 'quickAnalyses'),
      (snapshot) => {
        setQuickAnalyses(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as QuickAnalysis));
      },
      (err) => setError(err)
    );

    return () => {
      unsubQuickAnalyses();
    };
  }, [procurementId, isPanelChair]);

  // Separate effect for evaluations to handle RBAC filtering based on panel chair status
  useEffect(() => {
    if (!procurementId || !procurement || !account?.uid) {
      return;
    }

    // Evaluations listener
    // We constrain by evaluatorId for regular users, but Panel Chairs need to see all evaluations
    // for the moderation matrix. The Firestore rules allow Procurement Admins to read all.
    const baseCollection = collection(firestore, 'procurements', procurementId, 'evaluations');
    const evaluationsQuery = isPanelChair
      ? baseCollection
      : query(baseCollection, where('evaluatorId', '==', account.uid));

    const unsubEvaluations = onSnapshot(
      evaluationsQuery,
      (snapshot) => {
        setEvaluations(snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as Evaluation));
      },
      (err) => setError(err)
    );

    return () => {
      unsubEvaluations();
    };
  }, [procurementId, procurement, account?.uid, isPanelChair]);

  return {
    procurement,
    suppliers,
    rubric,
    evaluations,
    assessments,
    quickAnalyses,
    evaluatorAssignments,
    loading,
    error,
    isPanelChair,
  };
};
